import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Confirm,
  EditBase,
  Form,
  NumberInput,
  SaveButton,
  SelectInput,
  useDelete,
  useEditContext,
  useNotify,
  useSaveContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import accountSecurityErrorMessage, { isSsoOnlyAccount } from './accountSecurityErrors';
import {
  PASSWORD_POLICY_APPLIES_FROM_NOW_ON,
  PASSWORD_POLICY_FORM_DEFAULTS,
  PASSWORD_POLICY_RULE_LABELS,
  PASSWORD_POLICY_RULES,
  PasswordPolicyOverride,
  PasswordPolicyRule,
  PasswordPolicySettings as PasswordPolicySettingsResponse,
  boundsRange,
  buildPasswordPolicyUpdate,
  hasOverride,
  isCustomised,
  isWeakerThanDefault,
  resetToDefaultQuestion,
  validateWithinBounds,
  weakerThanDefaultWarning,
} from './passwordPolicySettings';

const ACCOUNT_SETTINGS_RESOURCE = 'accounts/settings';
const PASSWORD_POLICY_ID = 'password-policy';

type RuleInputProps = {
  rule: PasswordPolicyRule;
  settings: PasswordPolicySettingsResponse;
};

const useRuleInput = ({ rule, settings }: RuleInputProps) => {
  const defaultValue = settings.default[rule];
  const bounds = settings.bounds[rule];
  // NumberInput receives an empty value as '', SelectInput as null
  const format = useCallback(
    (value?: number | '' | null) => String(value === '' ? defaultValue : value ?? defaultValue),
    [defaultValue],
  );
  const validate = useMemo(() => validateWithinBounds(bounds), [bounds]);

  return { bounds, format, validate };
};

const MinLengthInput = ({ rule, settings }: RuleInputProps) => {
  const { bounds, format, validate } = useRuleInput({ rule, settings });

  return (
    <NumberInput
      source={`override.${rule}`}
      label={PASSWORD_POLICY_RULE_LABELS[rule]}
      format={format}
      validate={validate}
      helperText={`${bounds.min} to ${bounds.max} characters`}
      min={bounds.min}
      max={bounds.max}
      step={1}
      sx={{ width: 240 }}
    />
  );
};

const RequiredClassesInput = ({ rule, settings }: RuleInputProps) => {
  const { bounds, format, validate } = useRuleInput({ rule, settings });
  const choices = useMemo(() => boundsRange(bounds).map((count) => ({
    id: String(count),
    name: count === bounds.max ? `All ${count} types` : `${count} of ${bounds.max} types`,
  })), [bounds]);

  return (
    <SelectInput
      source={`override.${rule}`}
      label={PASSWORD_POLICY_RULE_LABELS[rule]}
      format={format}
      parse={Number}
      validate={validate}
      choices={choices}
      // Hides the empty choice: following the default is done with "Use default"
      isRequired
      helperText="Lowercase letters, uppercase letters, digits, special characters"
      sx={{ width: 240 }}
    />
  );
};

const ruleInputs: Record<PasswordPolicyRule, (props: RuleInputProps) => React.JSX.Element> = {
  minLength: MinLengthInput,
  requiredClasses: RequiredClassesInput,
};

const RuleRow = ({ rule, settings }: RuleInputProps) => {
  const { setValue } = useFormContext();
  const override = useWatch<{ override: PasswordPolicyOverride }, 'override'>({ name: 'override' });
  const customised = isCustomised(override, rule);
  const RuleInput = ruleInputs[rule];

  const followDefault = useCallback(() => {
    setValue(`override.${rule}`, null, { shouldDirty: true, shouldValidate: true });
  }, [setValue, rule]);

  return (
    <Box display="flex" alignItems="flex-start" gap={2}>
      <RuleInput rule={rule} settings={settings} />
      <Box display="flex" alignItems="center" gap={1} paddingTop={1}>
        <Chip label={customised ? 'Customised' : 'Pingwire default'} />
        {customised && (
          <Button variant="text" onClick={followDefault}>
            Use default
          </Button>
        )}
      </Box>
    </Box>
  );
};

const WeakerThanDefaultWarning = ({ settings }: { settings: PasswordPolicySettingsResponse }) => {
  const override = useWatch<{ override: PasswordPolicyOverride }, 'override'>({ name: 'override' });
  if (!isWeakerThanDefault(override, settings.default)) return null;

  return (
    <Alert severity="warning" sx={{ marginBottom: 2 }}>
      {weakerThanDefaultWarning(settings.default)}
    </Alert>
  );
};

const PasswordPolicyForm = ({ settings }: { settings: PasswordPolicySettingsResponse }) => {
  const notify = useNotify();
  const { refetch } = useEditContext();
  const { saving } = useSaveContext();
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [deletePolicy, { isPending: resetLoading }] = useDelete<PasswordPolicySettingsResponse>();

  const startReset = useCallback(() => {
    setConfirmingReset(true);
  }, []);

  const cancelReset = useCallback(() => {
    setConfirmingReset(false);
  }, []);

  const confirmReset = useCallback(() => {
    deletePolicy(
      ACCOUNT_SETTINGS_RESOURCE,
      { id: PASSWORD_POLICY_ID, previousData: settings },
      {
        onSuccess: () => {
          setConfirmingReset(false);
          refetch();
          notify('The password policy now follows Pingwire\'s default', { type: 'info' });
        },
        onError: (error) => {
          setConfirmingReset(false);
          notify(accountSecurityErrorMessage(error, 'Could not reset the password policy'), { type: 'error' });
        },
      },
    );
  }, [deletePolicy, settings, refetch, notify]);

  return (
    <Form defaultValues={PASSWORD_POLICY_FORM_DEFAULTS} mode="onChange">
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
        {PASSWORD_POLICY_APPLIES_FROM_NOW_ON}
      </Typography>
      {PASSWORD_POLICY_RULES.map((rule) => (
        <RuleRow key={rule} rule={rule} settings={settings} />
      ))}
      <WeakerThanDefaultWarning settings={settings} />
      <Box display="flex" gap={2}>
        <SaveButton icon={null} />
        <Button
          variant="outlined"
          onClick={startReset}
          disabled={saving || resetLoading || !hasOverride(settings)}
        >
          Reset all to default
        </Button>
      </Box>
      <Confirm
        isOpen={confirmingReset}
        loading={resetLoading}
        title="Reset the password policy?"
        content={resetToDefaultQuestion(settings.default)}
        confirm="Reset"
        onConfirm={confirmReset}
        onClose={cancelReset}
      />
    </Form>
  );
};

const PasswordPolicyCard = () => {
  const { record: settings, error, refetch } = useEditContext<PasswordPolicySettingsResponse>();

  if (isSsoOnlyAccount(error)) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Password policy
        </Typography>
        {settings && <PasswordPolicyForm settings={settings} />}
        {!settings && error && (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
          >
            {accountSecurityErrorMessage(error, 'Could not load the password policy')}
          </Alert>
        )}
        {!settings && !error && (
          <CircularProgress size={24} />
        )}
      </CardContent>
    </Card>
  );
};

const PasswordPolicySettings = () => {
  const notify = useNotify();

  return (
    <EditBase<PasswordPolicySettingsResponse>
      resource={ACCOUNT_SETTINGS_RESOURCE}
      id={PASSWORD_POLICY_ID}
      mutationMode="pessimistic"
      redirect={false}
      transform={buildPasswordPolicyUpdate}
      // Replaces react-admin's default "item doesn't exist" notification and redirect
      queryOptions={{ onError: () => undefined }}
      mutationOptions={{
        onSuccess: () => notify('The password policy has been saved', { type: 'info' }),
        onError: (error) => notify(accountSecurityErrorMessage(error, 'Could not save the password policy'), { type: 'error' }),
      }}
    >
      <PasswordPolicyCard />
    </EditBase>
  );
};

export default PasswordPolicySettings;
